/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtomValue, useStateXSetter } from '@cloudio-saas/statex';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Congrads from 'api/Congrads';
import React, { startTransition } from 'react';
import {
  activeSidebarPanelAtom,
  DSView,
  sidebarActiveDatasourceIdAtom,
  sidebarDatasourceEditModeAtom,
  sidebarPageViewAtom,
  sidebarPageWizardDataSourceAtom,
  sidebarPageWizardStepAtom,
  sidebarPageWizardTitleAtom,
} from '../../../atoms';
import { WizardOption } from '../../common';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    gap: '16px',
    backgroundColor: theme.palette.background.default,
  },
}));

const DataSourceWizardNext = ({
  setView,
  appUid,
}: {
  appUid: string;
  setView: (_view: DSView) => void;
}): React.ReactElement | null => {
  const ds = useStateXAtomValue(sidebarActiveDatasourceIdAtom);
  const classes = useStyles();
  const set = useStateXSetter();

  return (
    <div className={classes.root}>
      <Congrads msg={`DataSource '${ds}' successfully created!`} />
      <WizardOption
        data-testid="edit"
        iconName="edit"
        title="Edit DataSource"
        description="Navigate to DataSource Edit view"
        onClick={async () => {
          set(sidebarDatasourceEditModeAtom, 'Edit');
          setView('edit');
        }}
      />
      <WizardOption
        data-testid="attr"
        iconName="list"
        title="View Attributes"
        description="Navigate to DataSource Attributes view"
        onClick={async () => {
          set(sidebarDatasourceEditModeAtom, 'Edit');
          setView('listChildren');
        }}
      />
      <WizardOption
        data-testid="access"
        iconName="user-shield"
        title="Access Settings"
        description="Navigate to Access Settings view to grant access to this DataSource for various roles"
        onClick={async () => {
          startTransition(() => {
            set(sidebarDatasourceEditModeAtom, 'Edit');
            setView('access');
          });
        }}
      />
      <WizardOption
        data-testid="page"
        iconName="desktop"
        title="Create Page"
        description="Generate a new Page for this DataSource by choosing an existing Page Template"
        onClick={async () => {
          startTransition(() => {
            set(sidebarDatasourceEditModeAtom, 'Edit');
            set(sidebarPageWizardStepAtom, 'template');
            set(sidebarPageWizardDataSourceAtom, ds);
            set(sidebarPageViewAtom, 'wizard');
            set(activeSidebarPanelAtom, 'pages');
            set(sidebarPageWizardTitleAtom, 'Choose a Page Template');
            setView('list');
          });
        }}
      />
    </div>
  );
};

export default DataSourceWizardNext;
